/*
 * Lily & Minx - blog index navigation (lm-blog-index)
 * LAST UPDATED: 19/09/2026
 * ---------------------------------------------------------------------------
 * Progressive enhancement for sections/lm-blog-index.liquid. A topic or page
 * link inside the results region fetches the same URL through Shopify's
 * Section Rendering API (?section_id=) and swaps only [data-lm-blog-results].
 * The header is never touched, so the page H1 stays put.
 *
 * Contract:
 * - Every link keeps its real href. A failed or unexpected response falls
 *   back to normal navigation, never to a half-updated page.
 * - History changes only after a successful swap. Back and Forward restore
 *   the matching results. A newer request aborts the one in flight, and the
 *   section carries aria-busy while a request runs.
 * - After a swap the status region outside the results announces the new
 *   count. A topic keeps focus on the newly active topic; a page change
 *   moves focus to the results and scrolls them into view.
 * - Modified and middle clicks, targeted links and other origins are left
 *   to the browser. Listeners bind per section and are removed when the
 *   theme editor re-renders it.
 * - Publishes lm:blog-index:updated (bubbles, detail { url }).
 */

const RESULTS = '[data-lm-blog-results]';
const LINKS = 'a[data-lm-blog-filter], .pagination a[href]';

/* Links rendered through the Section Rendering API can carry section_id. A
   pushed URL, a fallback navigation or a new-tab link must never point at the
   bare section, so it is removed wherever a URL leaves this file. */
const pageUrl = (href) => {
  const url = new URL(href, window.location.href);
  url.searchParams.delete('section_id');
  url.searchParams.delete('sections');
  return url;
};

const locationKey = (url) => `${url.pathname}${url.search}`;

class LmBlogIndex extends HTMLElement {
  #controller = null;
  #shown = locationKey(pageUrl(window.location.href));
  #onClick = (event) => this.#handleClick(event);
  #onPopState = () => this.#handlePopState();

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#onPopState);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener('popstate', this.#onPopState);
    this.#controller?.abort();
  }

  #handleClick(event) {
    const link = event.target.closest(LINKS);
    if (!link || !link.closest(RESULTS)) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== '_self') return;

    const url = pageUrl(link.href);
    if (url.origin !== window.location.origin) return;

    event.preventDefault();
    this.#load(url, { push: true, focus: link.matches('[data-lm-blog-filter]') ? 'topic' : 'results' });
  }

  #handlePopState() {
    const url = pageUrl(window.location.href);
    if (locationKey(url) === this.#shown) return;

    const blog = new URL(this.dataset.blogUrl || '/', window.location.origin).pathname;
    if (url.pathname === blog || url.pathname.startsWith(`${blog}/tagged/`)) {
      this.#load(url, { push: false });
    }
  }

  async #load(url, { push, focus }) {
    const current = this.querySelector(RESULTS);
    const sectionId = this.dataset.sectionId;
    if (!current || !sectionId) {
      window.location.assign(url.href);
      return;
    }

    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    this.setAttribute('aria-busy', 'true');

    try {
      const request = new URL(url);
      request.searchParams.set('section_id', sectionId);
      const response = await fetch(request, { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Blog index request failed with ${response.status}`);

      const incoming = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector(RESULTS);
      if (!incoming) throw new Error('The response has no blog index results');

      for (const link of incoming.querySelectorAll('a[href]')) {
        const href = link.getAttribute('href');
        if (!/[?&](section_id|sections)=/.test(href)) continue;
        const clean = pageUrl(href);
        link.setAttribute('href', `${clean.pathname}${clean.search}${clean.hash}`);
      }

      current.replaceWith(incoming);
      this.#shown = locationKey(url);
      if (push) window.history.pushState({ lmBlogIndex: sectionId }, '', url.href);

      const status = this.querySelector('[data-lm-blog-status]');
      if (status) status.textContent = incoming.dataset.lmBlogSummary ?? '';

      if (focus === 'topic') {
        incoming.querySelector('[data-lm-blog-filter][aria-current="page"]')?.focus({ preventScroll: true });
      } else if (focus === 'results') {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        incoming.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
        incoming.focus({ preventScroll: true });
      }

      this.dispatchEvent(new CustomEvent('lm:blog-index:updated', { bubbles: true, detail: { url: url.href } }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(url.href);
    } finally {
      if (this.#controller === controller) {
        this.#controller = null;
        this.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('lm-blog-index')) {
  customElements.define('lm-blog-index', LmBlogIndex);
}
